import styled from "@emotion/styled";
import { type CSSProperties, type FC, useRef, useState } from "react";
import Button from "../../atom/button/Button";
import { Modal } from "../Modal";

export interface AnnouncementPopupProps {
  title: string;
  body: string;
  confirmText: string;
  cancelText: string;
  onConfirm: () => void;
  onCancel: () => void;
  image?: string;
  imageAlt?: string;
}

//#region Styled Components

// `Card` is now the panel rendered *inside* the shared Modal (alert variant),
// so it no longer needs to position itself — the Modal handles overlay,
// centering, focus scope and portal.
const Card = styled.div`
  label: AnnouncementPopupCard;
  background-color: #fff;
  overflow: hidden;
  width: 100%;
  position: relative;
`;

const GradientHeader = styled.div`
  label: AnnouncementPopupGradientHeader;
  background: var(
    --gradient-sunrise-light,
    linear-gradient(135deg, #a6c6ff 13.3%, #fad0e8 48.44%, #ffd199 81.45%)
  );
  height: 166px;
  position: relative;
`;

const HeaderImage = styled.img`
  label: AnnouncementPopupHeaderImage;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
`;

const CurveTransition = styled.svg`
  label: AnnouncementPopupCurve;
  display: block;
  transform: translateY(-100%);
  position: absolute;
`;

const ContentSection = styled.div`
  label: AnnouncementPopupContent;
  background: #fff;
  padding: 1rem 1.5rem 1rem;
  text-align: center;
`;

const Title = styled.h2`
  color: var(--Greys-Charcoal, #212121);
  text-align: center;
  font-family: var(--font-family-serif, "STK Bureau Serif");
  font-size: 28px;
  font-style: normal;
  font-weight: 300;
  line-height: 120%;
  letter-spacing: -0.56px;
  margin: 0 0 0.75rem;
`;

const Body = styled.p`
  color: var(--Greys-Charcoal80, rgba(33, 33, 33, 0.8));
  text-align: center;
  font-family: var(--font-family, "STK Bureau");
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: -0.32px;
  margin: 0 0 1.6rem;
`;

const CancelButton = styled.button`
  label: AnnouncementPopupCancel;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-charcoal, #212121);
  padding: 0.875rem 1rem;
  margin-top: 0.5rem;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;
//#endregion

// Pin the alert card to the AnnouncementPopup design (345px white rounded).
const MODAL_STYLE = {
  "--dialog-background": "#ffffff",
  "--border-radius": "1.5rem",
  "--width": "345px",
  "--max-width": "345px"
} as CSSProperties;

/**
 * Bespoke "announcement" overlay (gradient header + serif title + CTA) that
 * now runs on the shared `Modal` (alert variant), so it stacks correctly with
 * other overlays — the previous standalone `<FocusScope contain>` marked
 * sibling overlays as `inert`, blocking interaction when stacked.
 *
 * Consumed via conditional mount; on confirm/cancel we flip an internal
 * `isOpen` state to let the Modal play its exit animation, then forward the
 * caller's handler from `onDidDismiss`.
 */
const AnnouncementPopup: FC<AnnouncementPopupProps> = ({
  title,
  body,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  image,
  imageAlt = ""
}) => {
  const [isOpen, setIsOpen] = useState(true);
  // Tracks which button the user pressed so we can fire the matching handler
  // after the exit animation. Defaults to "cancel" — covers backdrop / Escape.
  const pendingAction = useRef<"confirm" | "cancel">("cancel");

  const handleConfirm = (): void => {
    pendingAction.current = "confirm";
    setIsOpen(false);
  };

  const handleCancel = (): void => {
    pendingAction.current = "cancel";
    setIsOpen(false);
  };

  const handleDidDismiss = (): void => {
    if (pendingAction.current === "confirm") {
      onConfirm();
    } else {
      onCancel();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      variant="alert"
      style={MODAL_STYLE}
      onDidDismiss={handleDidDismiss}
      ariaLabel={title}
    >
      <Card>
        <GradientHeader>
          {image && <HeaderImage src={image} alt={imageAlt} />}
        </GradientHeader>
        <CurveTransition
          viewBox="0 0 345 40"
          preserveAspectRatio="none"
          width="100%"
          height="40"
          aria-hidden="true"
        >
          <path d="M0,40 Q172.5,0 345,40 Z" fill="#fff" />
        </CurveTransition>
        <ContentSection>
          <Title>{title}</Title>
          <Body>{body}</Body>
          <Button fullWidth onPress={handleConfirm}>
            {confirmText}
          </Button>
          <CancelButton type="button" onClick={handleCancel}>
            {cancelText}
          </CancelButton>
        </ContentSection>
      </Card>
    </Modal>
  );
};

export default AnnouncementPopup;
